/**
 * ⭐ CADASTROS PELO LINK — o lado do Hub. 16/09/2026 (PRD §43.11).
 *
 * Desenho aprovado em `docs/sistema-visual-2026/cadastro-16-09/` (h1, h2, h3).
 *
 * - **Link de cadastro:** apelido que só a KAIRÓS vê → o link e a mensagem
 *   pronta. ⛔ O Hub não manda nada: quem manda é a pessoa, pelo canal de
 *   costume (a integração com WhatsApp está fora — PRD §4.3).
 * - **Pelo link** (era "Cadastros pelo link"): *chegaram · a conferir*, os
 *   *pedidos de pesquisa* (17/09/2026, abrem na ficha do cliente) e
 *   *aguardando o cliente*.
 * - **Conferir e cadastrar:** o Novo cliente em passo a passo, já preenchido.
 *   ⛔ Nada entra na carteira sem gravar.
 * - **Já é cliente:** só o VAZIO se completa; o diferente aparece e fica como
 *   está (a regra "nenhum botão apaga", 15/09/2026).
 *
 * ⛔ Tudo aqui é do master — a tela nem aparece para o colaborador, e o servidor
 * recusa as rotas (`exigirMaster`).
 */
import { useCallback, useEffect, useState } from 'react';
import {
  ErroDaApi,
  apiFormulario,
  type CadastroAConferir,
  type ContatoDoCadastro,
  type DadosDoCadastro,
  type EnvioDeCadastro,
  type LinkAguardando,
  type PedidoNaLista,
} from '../lib/api.ts';
import { pedirParaAbrirNaFicha } from '../lib/pedido-a-abrir.ts';
import { maiuscula } from '../lib/caixa.ts';
import { usarDialogo } from '../lib/dialogo.ts';
import { usarFormulario } from '../lib/formulario.ts';
import { mascararDocumento } from '../lib/rotulos.ts';
import { avisar, avisarErro } from './Avisos.tsx';
import { Confirmar } from './Confirmar.tsx';
import { GuardaDoFormulario } from './GuardaDoFormulario.tsx';
import { Icone } from './Icone.tsx';
import { AjudaDoPasso, CampoDoPasso, PassosDoTopo, Progresso, ResumoDoPasso, UFS, mascaraCep, soDigitos, type MarcaDoCampo } from './PassoAPasso.tsx';
import '../estilos/cadastro-link.css';

const FUSO = 'America/Sao_Paulo';
const dia = (iso: string) => new Intl.DateTimeFormat('pt-BR', { timeZone: FUSO, day: '2-digit', month: '2-digit' }).format(new Date(iso));
const diaAno = (iso: string) => new Intl.DateTimeFormat('pt-BR', { timeZone: FUSO, day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(iso));
const hora = (iso: string) => new Intl.DateTimeFormat('pt-BR', { timeZone: FUSO, hour: '2-digit', minute: '2-digit' }).format(new Date(iso));

export function copiar(texto: string, oQue: string) {
  if (!navigator.clipboard) {
    avisar('Este navegador não deixou copiar. Selecione o texto e copie à mão.', 'ruim');
    return;
  }
  void navigator.clipboard
    .writeText(texto)
    .then(() => avisar(`${oQue} copiado.`))
    .catch(() => avisar('Não consegui copiar. Selecione o texto e copie à mão.', 'ruim'));
}

function mensagemPronta(endereco: string, expiraEm: string | null) {
  return (
    'Olá! Para formalizarmos a contratação, preencha o cadastro da sua empresa neste link seguro.' +
    (expiraEm ? ` Ele vale até ${diaAno(expiraEm)}` : '') +
    ' e leva cerca de cinco minutos:\n' +
    `${endereco}\n` +
    '— KAIRÓS Asset Intelligence'
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// O painel
// ═══════════════════════════════════════════════════════════════════════════

export function CadastrosPeloLink({
  gerando,
  aoFecharGerar,
  aoAbrirCliente,
  aoMudarCarteira,
  aoExpirarSessao,
}: {
  gerando: boolean;
  aoFecharGerar: () => void;
  aoAbrirCliente: (id: string) => void;
  aoMudarCarteira: () => void;
  aoExpirarSessao: () => void;
}) {
  const [lista, setLista] = useState<{ ligado: boolean; aConferir: CadastroAConferir[]; aguardando: LinkAguardando[]; pedidos?: PedidoNaLista[] } | null>(null);
  const [mostrando, setMostrando] = useState<{ apelido: string; endereco: string; expiraEm: string | null } | null>(null);
  const [conferindo, setConferindo] = useState<string | null>(null);
  const [cancelando, setCancelando] = useState<LinkAguardando | null>(null);

  const carregar = useCallback(async () => {
    try {
      setLista(await apiFormulario.cadastros());
    } catch (e) {
      if (e instanceof ErroDaApi && e.status === 401) aoExpirarSessao();
    }
  }, [aoExpirarSessao]);

  useEffect(() => {
    void carregar();
    // O envio pode chegar com a tela aberta: olha de novo a cada minuto.
    const t = setInterval(() => void carregar(), 60_000);
    return () => clearInterval(t);
  }, [carregar]);

  async function copiarDeNovo(l: LinkAguardando) {
    try {
      const r = await apiFormulario.endereco(l.id);
      setMostrando({ apelido: l.apelido, endereco: r.endereco, expiraEm: r.expiraEm });
    } catch (e) {
      avisarErro(e);
      void carregar();
    }
  }

  const pedidos = lista?.pedidos ?? [];
  const nada = !lista || (lista.aConferir.length === 0 && lista.aguardando.length === 0 && pedidos.length === 0);

  return (
    <>
      {!nada && lista && (
        <section className="s-folha cad-painel" aria-label="Cadastros pelo link">
          <div className="cad-painel-topo">
            <div>
              {/* ⭐ "Pelo link" desde 17/09/2026: os pedidos de pesquisa entram aqui também. */}
              <span className="s-sobrelinha">Pelo link</span>
              <h2>O que os clientes preencheram</h2>
              <p>Nada entra na carteira sozinho: cada envio passa pela sua conferência.</p>
            </div>
          </div>

          {lista.aConferir.length > 0 && (
            <div className="cad-grupo">
              <div className="cad-grupo-titulo">
                <span className="s-micro">Chegaram · a conferir</span>
                <span className="cad-conta">{lista.aConferir.length}</span>
              </div>
              {lista.aConferir.map((e) => (
                <div key={e.id} className="cad-linha chegou">
                  <span className="cad-ponto" />
                  <div>
                    <p className="cad-nome">{e.nome.toLocaleUpperCase('pt-BR')}</p>
                    <p className="cad-meta">
                      <span className="dado">{e.documento}</span>
                      {e.cidadeUf && <span>{e.cidadeUf.toLocaleUpperCase('pt-BR')}</span>}
                      {e.clienteExistente ? (
                        <span className="cad-etiqueta ouro">
                          já é cliente
                          {e.clienteExistente.diferencas > 0 ? ` · ${e.clienteExistente.diferencas} campo(s) diferente(s)` : ''}
                        </span>
                      ) : (
                        <span className="cad-etiqueta">cliente novo</span>
                      )}
                    </p>
                  </div>
                  <span className="cad-quando">
                    chegou <b>{dia(e.recebidoEm)} às {hora(e.recebidoEm)}</b>
                    <br />
                    link: {e.apelido}
                  </span>
                  <span className="cad-acoes">
                    {e.clienteExistente ? (
                      <button type="button" className="botao-secundario s-botao s-botao-p" onClick={() => setConferindo(e.id)}>
                        Conferir o que mudou
                      </button>
                    ) : (
                      <button type="button" className="botao-primario s-botao s-botao-p" onClick={() => setConferindo(e.id)}>
                        Conferir e cadastrar <Icone nome="avancar" tamanho={14} />
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}

          {pedidos.length > 0 && (
            <div className="cad-grupo">
              <div className="cad-grupo-titulo">
                <span className="s-micro">Pedidos de pesquisa · a conferir</span>
                <span className="cad-conta">{pedidos.length}</span>
              </div>
              {/* ⛔ Sem nome de investigado nesta lista: a aba Clientes é tela de reunião (o servidor nem o manda). */}
              {pedidos.map((p) => (
                <div key={p.id} className="cad-linha chegou">
                  <span className="cad-ponto" />
                  <div>
                    <p className="cad-nome">{p.cliente.nome}</p>
                    <p className="cad-meta">
                      <span>
                        {p.pesquisas} {p.pesquisas === 1 ? 'pesquisa' : 'pesquisas'} · {p.investigados} {p.investigados === 1 ? 'investigado' : 'investigados'}
                      </span>
                      <span className="cad-etiqueta">pedido de pesquisa</span>
                    </p>
                  </div>
                  <span className="cad-quando">
                    chegou <b>{dia(p.recebidoEm)} às {hora(p.recebidoEm)}</b>
                    {p.quem && (
                      <>
                        <br />
                        pedido por {p.quem}
                      </>
                    )}
                  </span>
                  <span className="cad-acoes">
                    <button
                      type="button"
                      className="botao-primario s-botao s-botao-p"
                      onClick={() => {
                        pedirParaAbrirNaFicha(p.id, p.cliente.id);
                        aoAbrirCliente(p.cliente.id);
                      }}
                    >
                      Conferir o pedido <Icone nome="avancar" tamanho={14} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}

          {lista.aguardando.length > 0 && (
            <div className="cad-grupo">
              <div className="cad-grupo-titulo">
                <span className="s-micro">Aguardando o cliente</span>
                <span className="cad-conta fraca">{lista.aguardando.length}</span>
              </div>
              {lista.aguardando.map((l) => (
                <div key={l.id} className={`cad-linha ${l.situacao === 'aberto' ? 'aguarda' : 'vencido'}`}>
                  <span className="cad-ponto" />
                  <div>
                    <p className="cad-nome">{l.apelido}</p>
                    <p className="cad-meta">
                      <span>apelido que só a KAIRÓS vê</span>
                    </p>
                  </div>
                  <span className="cad-quando">
                    gerado <b>{dia(l.criadoEm)}</b>
                    {l.expiraEm && (
                      <>
                        {' · '}
                        {l.situacao === 'vencido' ? 'venceu em ' : 'vale até '}
                        <b>{dia(l.expiraEm)}</b>
                      </>
                    )}
                  </span>
                  <span className="cad-acoes">
                    {l.situacao === 'aberto' ? (
                      <>
                        <button type="button" className="botao-texto s-botao s-botao-p" onClick={() => void copiarDeNovo(l)}>
                          <Icone nome="copiar" tamanho={14} /> Copiar de novo
                        </button>
                        <button type="button" className="botao-texto botao-perigo s-botao s-botao-p" onClick={() => setCancelando(l)}>
                          Cancelar link
                        </button>
                      </>
                    ) : (
                      <button type="button" className="botao-texto s-botao s-botao-p" onClick={() => setCancelando(l)}>
                        Tirar da lista
                      </button>
                    )}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {gerando && (
        <DialogoDoLink
          aoFechar={() => {
            aoFecharGerar();
            void carregar();
          }}
        />
      )}
      {mostrando && <DialogoDoLink jaGerado={mostrando} aoFechar={() => setMostrando(null)} />}

      {cancelando && (
        <Confirmar
          titulo={cancelando.situacao === 'aberto' ? 'Cancelar este link?' : 'Tirar da lista?'}
          descricao={
            cancelando.situacao === 'aberto'
              ? `O link "${cancelando.apelido}" deixa de abrir na hora. Se o cliente ainda precisar preencher, gere outro.`
              : `O link "${cancelando.apelido}" já venceu. Ele sai desta lista.`
          }
          rotuloAcao={cancelando.situacao === 'aberto' ? 'Cancelar link' : 'Tirar da lista'}
          aoFechar={() => setCancelando(null)}
          aoConfirmar={async () => {
            try {
              await apiFormulario.cancelar(cancelando.id);
              avisar(cancelando.situacao === 'aberto' ? 'Link cancelado.' : 'Tirado da lista.');
            } catch (e) {
              avisarErro(e);
            }
            setCancelando(null);
            void carregar();
          }}
        />
      )}

      {conferindo && (
        <ConferirEnvio
          envioId={conferindo}
          aoFechar={() => setConferindo(null)}
          aoResolvido={(clienteId) => {
            setConferindo(null);
            void carregar();
            aoMudarCarteira();
            if (clienteId) aoAbrirCliente(clienteId);
          }}
        />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Gerar / mostrar o link
// ═══════════════════════════════════════════════════════════════════════════

const REGRAS = [
  <>
    Vale <b>7 dias</b> e se encerra quando o cliente enviar.
  </>,
  <>A página não mostra o nome de ninguém: link encaminhado por engano não revela quem contratou.</>,
  <>
    Pode ser copiado de novo enquanto valer. <b>Cancelar</b> o desliga na hora.
  </>,
];

function DialogoDoLink({
  jaGerado,
  aoFechar,
}: {
  jaGerado?: { apelido: string; endereco: string; expiraEm: string | null };
  aoFechar: () => void;
}) {
  usarDialogo(aoFechar);
  const [apelido, setApelido] = useState('');
  const [gerado, setGerado] = useState(jaGerado ?? null);
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function gerar() {
    if (apelido.trim().length < 2) return setErro('Dê um apelido ao link — é como você vai reconhecer o envio.');
    setGerando(true);
    setErro(null);
    try {
      const r = await apiFormulario.criarLink(apelido.trim());
      setGerado({ apelido: r.apelido, endereco: r.endereco, expiraEm: r.expiraEm });
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui gerar o link.');
    } finally {
      setGerando(false);
    }
  }

  return (
    <div className="cortina cad-cortina" onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="dialogo cad-dialogo" role="dialog" aria-modal="true" aria-label="Link de cadastro">
        <h2 className="titulo-secao">Link de cadastro</h2>
        <div className="cad-dialogo-corpo imv">
          {erro && <p className="erro">{erro}</p>}
          {!gerado ? (
            <div className="imv-grade-campos">
              <CampoDoPasso rotulo="Apelido do link" obrigatorio col={6} nota="Só a KAIRÓS vê. Serve para reconhecer o envio quando ele chegar.">
                <input
                  autoFocus
                  value={apelido}
                  maxLength={80}
                  placeholder="Ex.: indicação do Dr. Fulano"
                  onChange={(e) => setApelido(maiuscula(e.target.value))}
                  onKeyDown={(e) => e.key === 'Enter' && void gerar()}
                />
              </CampoDoPasso>
            </div>
          ) : (
            <>
              <p className="cad-apelido">
                <span className="imv-rotulo">Apelido</span> {gerado.apelido}
              </p>
              <div>
                <span className="imv-rotulo">Link gerado</span>
                <div className="cad-link">
                  <code>{gerado.endereco}</code>
                  <button type="button" className="botao-primario s-botao s-botao-p" onClick={() => copiar(gerado.endereco, 'Link')}>
                    <Icone nome="copiar" tamanho={14} /> Copiar link
                  </button>
                </div>
              </div>
              <div className="cad-mensagem">
                <span className="imv-rotulo">Mensagem pronta para mandar</span>
                <p>{mensagemPronta(gerado.endereco, gerado.expiraEm)}</p>
                <button
                  type="button"
                  className="botao-secundario s-botao s-botao-p"
                  onClick={() => copiar(mensagemPronta(gerado.endereco, gerado.expiraEm), 'Mensagem')}
                >
                  <Icone nome="copiar" tamanho={14} /> Copiar mensagem
                </button>
              </div>
            </>
          )}
          <ul className="cad-regras">
            {REGRAS.map((r, i) => (
              <li key={i}>
                <Icone nome="check" tamanho={14} />
                <span>{r}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="dialogo-acoes">
          {!gerado ? (
            <>
              <button type="button" className="botao-secundario" onClick={aoFechar}>
                Cancelar
              </button>
              <button type="button" className="botao-primario" disabled={gerando} onClick={() => void gerar()}>
                {gerando ? 'Gerando…' : 'Gerar link'}
              </button>
            </>
          ) : (
            <button type="button" className="botao-primario" onClick={aoFechar}>
              Pronto
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Conferir o que chegou
// ═══════════════════════════════════════════════════════════════════════════

function ConferirEnvio({ envioId, aoFechar, aoResolvido }: { envioId: string; aoFechar: () => void; aoResolvido: (clienteId: string | null) => void }) {
  const [envio, setEnvio] = useState<EnvioDeCadastro | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    apiFormulario
      .envio(envioId)
      .then(setEnvio)
      .catch((e) => setErro(e instanceof Error ? e.message : 'Não consegui abrir o envio.'));
  }, [envioId]);

  if (erro) {
    return <AvisoSimples titulo="Envio" texto={erro} aoFechar={aoFechar} />;
  }
  if (!envio) {
    return (
      <div className="cortina cad-cortina">
        <div className="dialogo">Abrindo o envio…</div>
      </div>
    );
  }
  return envio.clienteExistente ? (
    <CompletarCliente envio={envio} aoFechar={aoFechar} aoResolvido={aoResolvido} />
  ) : (
    <ConferirCadastro envio={envio} aoFechar={aoFechar} aoResolvido={aoResolvido} />
  );
}

export function AvisoSimples({ titulo, texto, aoFechar }: { titulo: string; texto: string; aoFechar: () => void }) {
  usarDialogo(aoFechar);
  return (
    <div className="cortina cad-cortina" onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="dialogo" role="dialog" aria-modal="true" aria-label={titulo}>
        <h2 className="titulo-secao">{titulo}</h2>
        <p>{texto}</p>
        <div className="dialogo-acoes">
          <button type="button" className="botao-primario" onClick={aoFechar}>
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

const PASSOS = ['A empresa', 'Endereço', 'Contato', 'Quem assina', 'Para terminar', 'Conferir e gravar'] as const;
const PASSO_DO_CAMPO: Record<string, number> = {
  tipo: 0, documento: 0, nome: 0, nomeFantasia: 0, origem: 0, site: 0,
  cep: 1, logradouro: 1, numero: 1, complemento: 1, bairro: 1, cidade: 1, uf: 1,
  email: 2, telefone: 2, contatos: 2,
  representanteNome: 3, representanteCpf: 3, representanteCargo: 3,
  observacao: 4,
};
const dig = soDigitos;
const cep = mascaraCep;

function ConferirCadastro({ envio, aoFechar, aoResolvido }: { envio: EnvioDeCadastro; aoFechar: () => void; aoResolvido: (clienteId: string | null) => void }) {
  const original = envio.envio.dados;
  const [d, setD] = useState<DadosDoCadastro>(() => ({
    ...original,
    documento: mascararDocumento(original.documento, original.tipo),
    representanteCpf: original.representanteCpf ? mascararDocumento(original.representanteCpf, 'pf') : '',
    cep: cep(original.cep),
    nome: maiuscula(original.nome),
    uf: original.uf.toUpperCase(),
  }));
  const [passo, setPasso] = useState(0);
  const [alcancado, setAlcancado] = useState(0);
  const [erro, setErro] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);
  const [descartando, setDescartando] = useState(false);
  const guarda = usarFormulario(d, aoFechar);
  usarDialogo(guarda.fechar);

  const pj = d.tipo === 'pj';
  const receita = envio.receita;
  const pos = (campo: string) => PASSO_DO_CAMPO[campo] ?? 0;

  const muda = <K extends keyof DadosDoCadastro>(k: K, v: DadosDoCadastro[K]) => setD((x) => ({ ...x, [k]: v }));
  const marca = (k: keyof DadosDoCadastro): MarcaDoCampo => {
    if (k === 'documento' && receita.conferido) return 'lido';
    const v = String(d[k] ?? '').trim();
    const o = String(original[k] ?? '').trim();
    return v && dig(v) === dig(o) && v.replace(/\W/g, '').toUpperCase() === o.replace(/\W/g, '').toUpperCase() ? 'veio' : undefined;
  };
  const texto = (k: keyof DadosDoCadastro, rotulo: string, col: 1 | 2 | 3 | 4 | 6, extra: { obrigatorio?: boolean; mono?: boolean; caixa?: boolean; nota?: string; max?: number; tipo?: string; mascara?: (v: string) => string } = {}) => (
    <CampoDoPasso rotulo={rotulo} obrigatorio={extra.obrigatorio} col={col} mono={extra.mono} marca={marca(k)} nota={extra.nota}>
      <input
        type={extra.tipo ?? 'text'}
        value={String(d[k] ?? '')}
        maxLength={extra.max ?? 200}
        onChange={(e) => {
          let v = e.target.value;
          if (extra.mascara) v = extra.mascara(v);
          else if (extra.caixa) v = maiuscula(v);
          muda(k, v as never);
        }}
      />
    </CampoDoPasso>
  );

  function problema(p: number): string | null {
    if (p === 0) {
      if (dig(d.documento).length !== (pj ? 14 : 11)) return pj ? 'Confira o CNPJ.' : 'Confira o CPF.';
      if (d.nome.trim().length < 2) return 'Informe o nome.';
    }
    if (p === 1) {
      if (dig(d.cep).length !== 8) return 'O CEP tem oito números.';
      if (!d.logradouro.trim() || !d.numero.trim() || !d.bairro.trim() || !d.cidade.trim()) return 'Complete o endereço.';
      if (!UFS.includes(d.uf)) return 'Escolha a UF.';
    }
    if (p === 2) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(d.email.trim())) return 'Confira o e-mail.';
      if (dig(d.telefone).length < 8) return 'Informe o telefone.';
      if (d.contatos.some((c) => !c.nome.trim())) return 'Todo contato precisa de nome (ou remova o bloco).';
    }
    if (p === 3 && pj) {
      if (d.representanteNome.trim().length < 2 || dig(d.representanteCpf).length !== 11 || d.representanteCargo.trim().length < 2) return 'Complete quem assina: nome, CPF e cargo.';
    }
    return null;
  }

  function irPara(p: number) {
    setErro(null);
    setPasso(p);
    setAlcancado((a) => Math.max(a, p));
  }
  function avancar() {
    const x = problema(passo);
    if (x) return setErro(x);
    irPara(passo + 1);
  }

  async function gravar() {
    for (let p = 0; p < 5; p++) {
      const x = problema(p);
      if (x) {
        setPasso(p);
        return setErro(x);
      }
    }
    setGravando(true);
    setErro(null);
    try {
      const dados: DadosDoCadastro = {
        ...d,
        documento: dig(d.documento),
        cep: dig(d.cep),
        representanteCpf: pj ? dig(d.representanteCpf) : '',
        representanteNome: pj ? d.representanteNome : '',
        representanteCargo: pj ? d.representanteCargo : '',
        contatos: d.contatos.filter((c) => c.nome.trim()),
      };
      const r = await apiFormulario.cadastrar(envio.envio.id, dados);
      guarda.marcarComoSalvo(d);
      avisar('Cliente cadastrado a partir do envio.');
      aoResolvido(r.clienteId);
    } catch (e) {
      setErro(e instanceof Error ? e.message : 'Não consegui gravar.');
    } finally {
      setGravando(false);
    }
  }

  const contato = (i: number, k: keyof ContatoDoCadastro, v: string | boolean) =>
    setD((x) => ({
      ...x,
      contatos: x.contatos.map((c, j) => (k === 'principal' ? { ...c, principal: j === i } : j === i ? { ...c, [k]: v } : c)),
    }));

  const recebido = envio.envio.recebidoEm;
  const diferencas =
    receita.difere.length > 0 ? (
      <div className="cad-mudou">
        <b>Diferente do cadastro da Receita Federal ({receita.difere.length}):</b>
        {receita.difere.map((x) => (
          <span key={x.campo}>
            {x.rotulo} (passo {pos(x.campo) + 1}) — o cliente escreveu <b>{x.noEnvio}</b>; a Receita traz <b>{x.naReceita}</b>. Vale o que ficar no campo.
          </span>
        ))}
      </div>
    ) : null;

  return (
    <div className="cortina cad-cortina" onMouseDown={(e) => e.target === e.currentTarget && guarda.fechar()}>
      <div className="imv cad-conferir" role="dialog" aria-modal="true" aria-label="Conferir cadastro">
        <section className="imv-ficha">
          <header className="imv-ficha-topo">
            <div>
              <span className="s-sobrelinha">
                Novo cliente · a partir do cadastro enviado · passo {passo + 1} de {PASSOS.length}
              </span>
              <h3 className="cad-titulo">Conferir e cadastrar {maiuscula(original.nome)}</h3>
              <p className="cad-sub">O mesmo passo a passo do Novo cliente, já preenchido. Nada entra na carteira antes de você gravar.</p>
            </div>
            <button type="button" className="imv-fechar" aria-label="Fechar" onClick={guarda.fechar}>
              <Icone nome="fechar" tamanho={16} />
            </button>
          </header>
          <div className="cad-chegada">
            <Icone nome="caixa" tamanho={18} />
            <span>
              <b>
                Chegou pelo link em {diaAno(recebido)} às {hora(recebido)}
              </b>{' '}
              · apelido {envio.envio.apelido} · o cliente marcou que leu o aviso de privacidade.
            </span>
          </div>
          <PassosDoTopo passos={PASSOS} atual={passo} alcancado={alcancado} aoIr={irPara} />

          <div className="imv-corpo">
            <div className="imv-conteudo">
              <p className="cad-legenda">
                <span>
                  <i /> veio do cadastro enviado — confira
                </span>
                {receita.conferido ? (
                  <span>
                    <i className="receita" /> conferido na Receita Federal ao abrir
                    {receita.situacao && !/ativa/i.test(receita.situacao) ? ` · situação: ${receita.situacao}` : ''}
                  </span>
                ) : (
                  pj && <span>não foi possível conferir na Receita Federal agora</span>
                )}
              </p>

              {passo === 0 && (
                <>
                  <div className="imv-seg" style={{ marginBottom: 14 }}>
                    {(['pj', 'pf'] as const).map((t) => (
                      <button key={t} type="button" className={`imv-op${d.tipo === t ? ' on' : ''}`} onClick={() => setD((x) => ({ ...x, tipo: t, documento: '' }))}>
                        <i />
                        {t === 'pj' ? 'Pessoa jurídica' : 'Pessoa física'}
                      </button>
                    ))}
                  </div>
                  <div className="imv-grade-campos">
                    {texto('documento', pj ? 'CNPJ' : 'CPF', 2, { obrigatorio: true, mono: true, max: 18, mascara: (v) => mascararDocumento(v, d.tipo) })}
                    <span className="imv-campo c4" />
                    {texto('nome', pj ? 'Razão social' : 'Nome completo', 4, { obrigatorio: true, caixa: true })}
                    {pj ? texto('nomeFantasia', 'Nome fantasia', 2, { caixa: true }) : <span className="imv-campo c2" />}
                    {texto('origem', 'Quem indicou', 3, { caixa: true })}
                    {texto('site', 'Site', 3)}
                  </div>
                  {diferencas}
                </>
              )}

              {passo === 1 && (
                <div className="imv-grade-campos">
                  {texto('cep', 'CEP', 2, { obrigatorio: true, mono: true, max: 9, mascara: cep })}
                  <span className="imv-campo c4" />
                  {texto('logradouro', 'Logradouro', 4, { obrigatorio: true, caixa: true })}
                  {texto('numero', 'Número', 1, { obrigatorio: true, caixa: true, max: 20 })}
                  {texto('complemento', 'Complemento', 1, { caixa: true, max: 100 })}
                  {texto('bairro', 'Bairro', 2, { obrigatorio: true, caixa: true, max: 100 })}
                  {texto('cidade', 'Cidade', 3, { obrigatorio: true, caixa: true, max: 100 })}
                  <CampoDoPasso rotulo="UF" obrigatorio col={1} marca={marca('uf')}>
                    <select value={d.uf} onChange={(e) => muda('uf', e.target.value)}>
                      <option value="">—</option>
                      {UFS.map((u) => (
                        <option key={u}>{u}</option>
                      ))}
                    </select>
                  </CampoDoPasso>
                </div>
              )}

              {passo === 2 && (
                <>
                  <div className="imv-grade-campos">
                    {texto('email', 'E-mail', 3, { obrigatorio: true, tipo: 'email' })}
                    {texto('telefone', 'Telefone', 2, { obrigatorio: true, mono: true, max: 40 })}
                  </div>
                  <div className="cad-subtitulo">
                    <h5>Contatos</h5>
                  </div>
                  {d.contatos.length === 0 && <p className="cad-nada">O cliente não indicou contatos.</p>}
                  {d.contatos.map((c, i) => (
                    <div key={i} className="cad-contato">
                      {c.principal && <span className="cad-principal">principal</span>}
                      <div className="imv-grade-campos">
                        <CampoDoPasso rotulo="Nome" obrigatorio col={3}>
                          <input value={c.nome} maxLength={120} onChange={(e) => contato(i, 'nome', maiuscula(e.target.value))} />
                        </CampoDoPasso>
                        <CampoDoPasso rotulo="Cargo" col={3}>
                          <input value={c.cargo} maxLength={120} onChange={(e) => contato(i, 'cargo', maiuscula(e.target.value))} />
                        </CampoDoPasso>
                        <CampoDoPasso rotulo="E-mail" col={3}>
                          <input type="email" value={c.email} maxLength={200} onChange={(e) => contato(i, 'email', e.target.value)} />
                        </CampoDoPasso>
                        <CampoDoPasso rotulo="Telefone" col={3} mono>
                          <input value={c.telefone} maxLength={40} onChange={(e) => contato(i, 'telefone', e.target.value)} />
                        </CampoDoPasso>
                      </div>
                      <div className="cad-contato-acoes">
                        {!c.principal && (
                          <button type="button" className="botao-texto s-botao s-botao-p" onClick={() => contato(i, 'principal', true)}>
                            Tornar principal
                          </button>
                        )}
                        <button type="button" className="botao-texto botao-perigo s-botao s-botao-p" onClick={() => setD((x) => ({ ...x, contatos: x.contatos.filter((_, j) => j !== i) }))}>
                          Não trazer este contato
                        </button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {passo === 3 &&
                (pj ? (
                  <div className="imv-grade-campos">
                    {texto('representanteNome', 'Nome de quem assina', 3, { obrigatorio: true, caixa: true })}
                    {texto('representanteCpf', 'CPF', 2, { obrigatorio: true, mono: true, max: 14, mascara: (v) => mascararDocumento(v, 'pf') })}
                    <span className="imv-campo c1" />
                    {texto('representanteCargo', 'Cargo', 3, { obrigatorio: true, caixa: true, max: 120 })}
                  </div>
                ) : (
                  <p className="cad-nada">Pessoa física: quem assina é o próprio cliente.</p>
                ))}

              {passo === 4 && (
                <div className="imv-grade-campos">
                  <CampoDoPasso rotulo="Observação (o que o cliente escreveu em “algo mais”)" col={6} marca={marca('observacao')}>
                    <textarea rows={4} value={d.observacao} maxLength={4000} onChange={(e) => muda('observacao', e.target.value)} />
                  </CampoDoPasso>
                </div>
              )}

              {passo === 5 && (
                <div className="cad-resumo">
                  <ResumoDoPasso titulo={pj ? 'A empresa' : 'O cliente'} aoCorrigir={() => irPara(0)} linhas={[[pj ? 'Razão social' : 'Nome', d.nome], ['Nome fantasia', d.nomeFantasia], [pj ? 'CNPJ' : 'CPF', d.documento], ['Quem indicou', d.origem], ['Site', d.site]]} />
                  <ResumoDoPasso titulo="Endereço" aoCorrigir={() => irPara(1)} linhas={[['Logradouro', [d.logradouro, d.numero].filter(Boolean).join(', ') + (d.complemento ? ` — ${d.complemento}` : '')], ['Bairro', d.bairro], ['Cidade', d.cidade ? `${d.cidade}/${d.uf}` : ''], ['CEP', d.cep]]} />
                  <ResumoDoPasso titulo="Contato" aoCorrigir={() => irPara(2)} linhas={[['E-mail', d.email], ['Telefone', d.telefone], ['Contatos', d.contatos.map((c) => `${c.nome}${c.principal ? ' (principal)' : ''}`).join(' · ')]]} />
                  <ResumoDoPasso titulo="Quem assina" aoCorrigir={() => irPara(3)} linhas={pj ? [['Nome', d.representanteNome], ['CPF', d.representanteCpf], ['Cargo', d.representanteCargo]] : [['Assina', d.nome]]} />
                  {diferencas && <div className="cad-resumo-inteiro">{diferencas}</div>}
                </div>
              )}
            </div>
            {passo < 5 && (
              <AjudaDoPasso titulo="Antes de gravar">
                <p>
                  Os campos com a barra <b>roxa</b> são o que o cliente escreveu.{receita.conferido ? ' O CNPJ foi conferido de novo na Receita ao abrir.' : ''}
                </p>
                <p>
                  Se preferir não cadastrar, <b>descarte o envio</b>: ele sai da lista e nada muda na carteira.
                </p>
              </AjudaDoPasso>
            )}
          </div>

          {erro && <p className="erro imv-erro">{erro}</p>}

          <footer className="imv-ficha-pe">
            <button type="button" className="botao-texto botao-perigo s-botao s-botao-p" onClick={() => setDescartando(true)}>
              Descartar este envio
            </button>
            <span className="imv-esp" />
            {passo > 0 && (
              <button type="button" className="imv-bt fantasma" onClick={() => irPara(passo - 1)}>
                <Icone nome="voltar" tamanho={14} /> Voltar
              </button>
            )}
            <Progresso atual={passo} total={PASSOS.length} />
            {passo < PASSOS.length - 1 ? (
              <button type="button" className="imv-bt primario" onClick={avancar}>
                Avançar <Icone nome="avancar" tamanho={14} />
              </button>
            ) : (
              <button type="button" className="imv-bt primario" disabled={gravando} onClick={() => void gravar()}>
                <Icone nome="check" tamanho={14} /> {gravando ? 'Gravando…' : 'Gravar cliente'}
              </button>
            )}
          </footer>
        </section>
      </div>

      <GuardaDoFormulario pendencia={guarda.pendencia} oQue="o cliente" aoCancelar={guarda.cancelarPendencia} aoSalvar={() => void gravar()} aoDescartar={guarda.aoFecharDeVerdade} />

      {descartando && (
        <Confirmar
          titulo="Descartar este envio?"
          descricao="Ele sai da lista e nada muda na carteira. O cliente não é avisado."
          rotuloAcao="Descartar"
          aoFechar={() => setDescartando(false)}
          aoConfirmar={async () => {
            try {
              await apiFormulario.descartar(envio.envio.id);
              avisar('Envio descartado.');
              aoResolvido(null);
            } catch (e) {
              avisarErro(e);
              setDescartando(false);
            }
          }}
        />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Já é cliente: completar o vazio
// ═══════════════════════════════════════════════════════════════════════════

function CompletarCliente({ envio, aoFechar, aoResolvido }: { envio: EnvioDeCadastro; aoFechar: () => void; aoResolvido: (clienteId: string | null) => void }) {
  usarDialogo(aoFechar);
  const c = envio.clienteExistente!;
  const [fazendo, setFazendo] = useState(false);
  const [descartando, setDescartando] = useState(false);
  const recebido = envio.envio.recebidoEm;

  async function completar() {
    setFazendo(true);
    try {
      const r = await apiFormulario.completar(envio.envio.id);
      avisar(
        r.completados || r.contatosNovos
          ? `Completado: ${r.completados} campo(s) e ${r.contatosNovos} contato(s) novo(s). Nada que já existia mudou.`
          : 'Nada a completar: o cadastro já tinha tudo. O envio saiu da lista.',
      );
      aoResolvido(c.id);
    } catch (e) {
      avisarErro(e);
      setFazendo(false);
    }
  }

  return (
    <div className="cortina cad-cortina" onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="dialogo cad-dialogo cad-largo" role="dialog" aria-modal="true" aria-label="Conferir o que mudou">
        <header className="cad-dialogo-topo">
          <span className="s-sobrelinha">Cadastro enviado · já é cliente</span>
          <h2 className="titulo-secao">{c.nome}</h2>
          <p className="cad-sub">
            Chegou pelo link em {diaAno(recebido)} às {hora(recebido)} · apelido {envio.envio.apelido}.{' '}
            <b>Nada do que a carteira já tem muda.</b>
          </p>
        </header>

        <div className="cad-dialogo-corpo">
          <section>
            <span className="s-micro">Vai completar (estava vazio)</span>
            {c.completa.length ? (
              <ul className="cad-lista">
                {c.completa.map((x) => (
                  <li key={x.campo}>{x.rotulo}</li>
                ))}
              </ul>
            ) : (
              <p className="cad-nada">Nenhum campo vazio a completar.</p>
            )}
            <p className="cad-nada">Contatos que ainda não estão na ficha entram também, sem trocar o principal.</p>
          </section>

          <section>
            <span className="s-micro">Veio diferente (fica como está)</span>
            {c.difere.length ? (
              <div className="cad-tabela">
                <div className="cad-tl cad-tl-cab">
                  <span>Campo</span>
                  <span>Na carteira</span>
                  <span>No envio</span>
                </div>
                {c.difere.map((x) => (
                  <div key={x.campo} className="cad-tl">
                    <span>{x.rotulo}</span>
                    <span>{x.naCarteira}</span>
                    <span>{x.noEnvio}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="cad-nada">Nada diferente.</p>
            )}
            {c.difere.length > 0 && <p className="cad-nada">Se o envio estiver certo, corrija pela ficha do cliente (Editar).</p>}
          </section>
        </div>

        <div className="dialogo-acoes">
          <button type="button" className="botao-texto botao-perigo" onClick={() => setDescartando(true)}>
            Descartar este envio
          </button>
          <span className="imv-esp" />
          <button type="button" className="botao-secundario" onClick={() => aoResolvido(c.id)}>
            Abrir a ficha
          </button>
          <button type="button" className="botao-primario" disabled={fazendo} onClick={() => void completar()}>
            {fazendo ? 'Completando…' : 'Completar o vazio'}
          </button>
        </div>
      </div>

      {descartando && (
        <Confirmar
          titulo="Descartar este envio?"
          descricao="Ele sai da lista e nada muda na carteira."
          rotuloAcao="Descartar"
          aoFechar={() => setDescartando(false)}
          aoConfirmar={async () => {
            try {
              await apiFormulario.descartar(envio.envio.id);
              avisar('Envio descartado.');
              aoResolvido(null);
            } catch (e) {
              avisarErro(e);
              setDescartando(false);
            }
          }}
        />
      )}
    </div>
  );
}
